// src/features/presence/tables-section.tsx
import { DataTable } from "@/components/iris";
import type { DataTableColumn } from "@/components/iris";
import { Kicker } from "@/components/iris/kicker";
import { CollapsibleCard } from "@/features/statistics/chart-primitives";
import { usePresenceSessions, usePresenceTopUsers } from "@/hooks/use-presence";
import { formatCount } from "@/lib/format-count";
import type { Env, PresenceSession, PresenceTopUser } from "@/lib/types";
import { useState } from "react";
import { formatDuration } from "./format";

interface TablesSectionProps {
  env: Env;
}

const PAGE_SIZE = 50;

const TOP_USER_COLUMNS: DataTableColumn<PresenceTopUser>[] = [
  { key: "account_id", label: "Account ID", cellVariant: "mono" },
  {
    key: "event_count",
    label: "Events",
    cellVariant: "num",
    headerAlign: "right",
    render: (row) => formatCount(row.event_count),
  },
];

function fmtTs(ts: string | null): string {
  if (!ts) return "—";
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? String(ts) : d.toLocaleString();
}

const SESSION_COLUMNS: DataTableColumn<PresenceSession>[] = [
  { key: "account_id", label: "Account", cellVariant: "mono" },
  { key: "client_type", label: "Client" },
  { key: "status", label: "Status" },
  { key: "started_at", label: "Started", cellVariant: "muted", render: (r) => fmtTs(r.started_at) },
  { key: "ended_at", label: "Ended", cellVariant: "muted", render: (r) => fmtTs(r.ended_at) },
  {
    key: "session_duration_sec",
    label: "Duration",
    cellVariant: "num",
    headerAlign: "right",
    render: (r) => formatDuration(r.session_duration_sec),
  },
  {
    key: "active_duration_sec",
    label: "Active",
    cellVariant: "num",
    headerAlign: "right",
    render: (r) => formatDuration(r.active_duration_sec),
  },
  {
    key: "event_count",
    label: "Events",
    cellVariant: "num",
    headerAlign: "right",
    render: (r) => formatCount(r.event_count),
  },
];

/**
 * IR-1148 §5 — Most Frequent Users + a paginated Session Explorer. Pagination
 * uses the IR-597 envelope (`pagination.has_more` / `total`) with prev/next.
 */
export function TablesSection({ env }: TablesSectionProps) {
  const [offset, setOffset] = useState(0);

  // Reset paging when env flips so dev/prod pages don't bleed together.
  const [prevEnv, setPrevEnv] = useState(env);
  if (prevEnv !== env) {
    setPrevEnv(env);
    setOffset(0);
  }

  const { data: topData, isLoading: topLoading } = usePresenceTopUsers(env);
  const { data: sessData, isLoading: sessLoading } = usePresenceSessions(env, {
    offset,
    limit: PAGE_SIZE,
  });

  const topUsers = topData?.data ?? [];
  const sessions = sessData?.data ?? [];
  const pagination = sessData?.pagination;
  const total = pagination?.total ?? 0;
  const hasMore = pagination?.has_more ?? false;
  const pageEnd = Math.min(offset + sessions.length, total);

  return (
    <>
      <Kicker style={{ marginTop: 32, marginBottom: 12 }}>Users &amp; Sessions</Kicker>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16 }}>
        <CollapsibleCard title="Most Frequent Users">
          {topLoading ? (
            <p className="iris-meta">Loading…</p>
          ) : (
            <DataTable
              columns={TOP_USER_COLUMNS}
              data={topUsers}
              rowKey={(row) => row.account_id}
              emptyMessage="No user activity yet"
            />
          )}
        </CollapsibleCard>

        <CollapsibleCard title="Session Explorer">
          {sessLoading ? (
            <p className="iris-meta">Loading…</p>
          ) : (
            <>
              <DataTable
                columns={SESSION_COLUMNS}
                data={sessions}
                rowKey={(row) => row.login_session_id}
                emptyMessage="No sessions in this window"
              />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: 12,
                  gap: 12,
                }}
              >
                <span className="iris-meta">
                  {total > 0 ? `${offset + 1}–${pageEnd} of ${formatCount(total)}` : "0 sessions"}
                </span>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    className="iris-btn"
                    disabled={offset === 0}
                    onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    className="iris-btn"
                    disabled={!hasMore}
                    onClick={() => setOffset((o) => o + PAGE_SIZE)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </CollapsibleCard>
      </div>
    </>
  );
}
